import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Field, Form, Input, TextArea, Typography } from '@/components';
import { CapexAndOpex, Project } from '@/features/projects/types';
import { formatStringAsInteger, nullableStringSetter, parseStringToInteger } from '@/helpers';
import { NUMERIC } from '@/features/projects/helpers/numeric-inputs';

export type CapexOpexFormFields = {
  estimated_epc: number | null;
  epc_comments: string | null;
  tsa: number | null;
  tsa_details: string | null;
  bop: number | null;
  bop_details: string | null;
  om_price: number | null;
  om_term: number | null;
  om_comments: string | null;
  other_comments: string | null;
};

export type CapexOpexFormPayload = {
  capex_and_opex: CapexOpexFormFields[];
};

export type CapexOpexFormProps = {
  readonly id: string;
  readonly project: Project;
  readonly onSubmit: (data: CapexOpexFormPayload) => void;
};

export function CapexOpexFormNew({ id, project, onSubmit }: CapexOpexFormProps) {
  const { t } = useTranslation(['projects']);

  const item = project.capex_and_opex?.[0] ?? ({} as Partial<CapexAndOpex>);

  const hasWind = project.technologies.some((tech) => tech.collection === 'projects_wind_technologies');

  const options = useMemo(
    () => ({
      defaultValues: {
        estimated_epc: item.estimated_epc ?? null,
        epc_comments: item.epc_comments ?? null,
        tsa: item.tsa ?? null,
        tsa_details: item.tsa_details ?? null,
        bop: item.bop ?? null,
        bop_details: item.bop_details ?? null,
        om_price: item.om_price ?? null,
        om_term: item.om_term ?? null,
        om_comments: item.om_comments ?? null,
        other_comments: item.other_comments ?? null,
      },
    }),
    [project]
  );

  return (
    <Form<CapexOpexFormFields> id={id} onSubmit={(data) => onSubmit({ capex_and_opex: [data] })} options={options}>
      {({ formState, register }) => (
        <div className="space-y-6">
          <Typography as="p" color="secondary" size="body-3" weight="normal" className="mt-1 italic">
            {t(
              "Please provide information about the project's CAPEX and OPEX in this section, including estimated EPC costs, O&M price and contract term, insurance price, and any relevant comments."
            )}
          </Typography>

          <Field
            id="estimated_epc"
            label={t('Estimated EPC')}
            error={formState.errors['estimated_epc']}
            delay={0}
            classes={{ field: 'space-y-1' }}
          >
            <Input
              id="estimated_epc"
              type="text"
              mask={formatStringAsInteger}
              placeholder={t('e.g. 15.000.000')}
              color={formState.errors['estimated_epc'] ? 'error' : 'primary'}
              suffix="€"
              registration={register('estimated_epc', { setValueAs: parseStringToInteger })}
            />
          </Field>

          <Field
            id="epc_comments"
            label={t('EPC comments')}
            error={formState.errors['epc_comments']}
            delay={0.15}
            classes={{ field: 'space-y-1' }}
          >
            <TextArea
              id="epc_comments"
              placeholder={t('Type here...')}
              color="primary"
              registration={register('epc_comments', { setValueAs: nullableStringSetter })}
            />
          </Field>

          {hasWind && (
            <>
              <Field
                id="tsa"
                label={t('TSA')}
                error={formState.errors['tsa']}
                delay={0.6}
                classes={{ field: 'space-y-1' }}
              >
                <Input
                  id="tsa"
                  type="number"
                  placeholder={t('e.g. 15.000.000')}
                  color={formState.errors['tsa'] ? 'error' : 'primary'}
                  suffix="€"
                  registration={register('tsa', { ...NUMERIC })}
                />
              </Field>

              <Field
                id="tsa_details"
                label={t('TSA details')}
                error={formState.errors['tsa_details']}
                delay={0.6}
                classes={{ field: 'space-y-1' }}
              >
                <TextArea
                  id="tsa_details"
                  placeholder={t('Type here...')}
                  color="primary"
                  registration={register('tsa_details', { setValueAs: nullableStringSetter })}
                />
              </Field>

              <Field
                id="bop"
                label={t('BOP')}
                error={formState.errors['bop']}
                delay={0.6}
                classes={{ field: 'space-y-1' }}
              >
                <Input
                  id="bop"
                  type="number"
                  placeholder={t('e.g. 15.000.000')}
                  color={formState.errors['bop'] ? 'error' : 'primary'}
                  suffix="€"
                  registration={register('bop', { ...NUMERIC })}
                />
              </Field>

              <Field
                id="bop_details"
                label={t('BOP details')}
                error={formState.errors['bop_details']}
                delay={0.6}
                classes={{ field: 'space-y-1' }}
              >
                <TextArea
                  id="bop_details"
                  placeholder={t('Type here...')}
                  color="primary"
                  registration={register('bop_details', { setValueAs: nullableStringSetter })}
                />
              </Field>
            </>
          )}

          <Field
            id="om_price"
            label={t('O&M price')}
            error={formState.errors['om_price']}
            delay={0.3}
            classes={{ field: 'space-y-1' }}
          >
            <Input
              id="om_price"
              type="number"
              placeholder={t('e.g. 15.000.000')}
              color="primary"
              suffix="€"
              registration={register('om_price', { ...NUMERIC })}
            />
          </Field>

          <Field
            id="om_term"
            label={t('O&M contract term')}
            error={formState.errors['om_term']}
            delay={0.45}
            classes={{ field: 'space-y-1' }}
          >
            <Input
              id="om_term"
              type="number"
              placeholder={t('e.g. 5')}
              color="primary"
              suffix="Years"
              registration={register('om_term', { ...NUMERIC })}
            />
          </Field>

          <Field
            id="om_comments"
            label={t('O&M comments')}
            error={formState.errors['om_comments']}
            delay={0.45}
            classes={{ field: 'space-y-1' }}
          >
            <TextArea
              id="om_comments"
              placeholder={t('Type here...')}
              color="primary"
              registration={register('om_comments', { setValueAs: nullableStringSetter })}
            />
          </Field>

          <Field
            id="other_comments"
            label={t('Other comments')}
            error={formState.errors['other_comments']}
            delay={0.45}
            classes={{ field: 'space-y-1' }}
          >
            <TextArea
              id="other_comments"
              placeholder={t('Type here...')}
              color="primary"
              registration={register('other_comments', { setValueAs: nullableStringSetter })}
            />
          </Field>
        </div>
      )}
    </Form>
  );
}
